import React, { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { useParams } from "react-router";
import { Card, Button, Badge } from "components/ui";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ClockIcon,
  UserIcon,
  CalendarDaysIcon,
  ListBulletIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  NoSymbolIcon,
  ClockIcon as PendingIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import {
  useLeadFollowUps,
  useFollowUpActions,
  useActivityTypes,
  useFollowUpBulkActions,
} from "hooks/useFollowUp";
import FollowUpScheduleModal from "../LeadActions/FollowUpScheduleModal";
import ActivityStatusChangeModal from "../LeadActions/ActivityStatusChangeModal";
import { toast } from "sonner";
import { invalidateNotesCache } from "hooks/useNotes";
import { ConfirmModal } from "components/shared/ConfirmModal";
import CustomCalendar from "custom-components/shared/CustomCalendar";
import CalendarDayCell from "custom-components/shared/CalendarDayCell";
import { FloatingPopoverTooltip } from "app/pages/myActivities/components/FloatingPopoverTooltip";
import { hasPermission } from "utils/permissionsUtils";
import {
  getLocalDateTime,
  formatTime,
  parseDate,
} from "../../../utils/dateUtils";

const formatTimeLocal = (timeStr) => {
  if (!timeStr) return "";
  if (/AM|PM/i.test(timeStr)) {
    return timeStr;
  }
  const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  return timeStr;
};

const CalendarTab = ({
  isMobile,
  leadData,
  initialDate,
  isStudent = false,
}) => {
  const { id: routeLeadId } = useParams();

  // Get leadId from props (when in modal) or route params (when in standalone page)
  const leadId =
    leadData?.id || leadData?.leadId || leadData?.LeadId || routeLeadId;

  const [currentDate, setCurrentDate] = useState(() => {
    if (initialDate) {
      const parsed = parseDate(initialDate);
      if (parsed) return parsed;
    }
    return new Date();
  });
  // ── Data via SWR hooks ──
  const {
    followUps,
    isLoading: loading,
    error: hookError,
    refresh: refreshFollowUps,
  } = useLeadFollowUps(leadId);

  const followUpActions = useFollowUpActions({ leadId });
  const { handleBulkAction } = useFollowUpBulkActions();

  // Map hook error to local error string for the error UI
  const error = hookError
    ? hookError.message || "Failed to load follow-ups"
    : null;

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [deleteActivityLoading, setDeleteActivityLoading] = useState(false);
  const [statusChangeActivity, setStatusChangeActivity] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const initialStatusId = useMemo(() => {
    if (!statusChangeActivity) return null;
    const activity = statusChangeActivity;
    const rawStatusId = activity.statusId || activity._raw?.StatusId || activity._raw?.statusId || activity.StatusId || activity.status_id;
    if (rawStatusId) return rawStatusId;

    const statusName = activity.status || activity.StatusName;
    if (statusName) {
      const statusMap = {
        pending: 1,
        completed: 2,
        missed: 3,
        canceled: 4,
        rescheduled: 5,
        overdue: 6
      };
      return statusMap[statusName.toLowerCase()] || null;
    }
    return null;
  }, [statusChangeActivity]);

  const confirmDeleteActivity = async () => {
    if (!activityToDelete) return;
    const activityId = activityToDelete.FollowUpId || activityToDelete.id || activityToDelete.ActivityId;
    if (!activityId) return;

    setDeleteActivityLoading(true);
    try {
      const result = await handleBulkAction({
        actionType: 2,
        followUpIds: [activityId],
      });
      if (result?.success) {
        toast.success(result.message || "Activity deleted successfully");
        setActivityToDelete(null);
        refreshFollowUps();
      } else {
        toast.error(result?.error || "Failed to delete activity");
      }
    } catch (err) {
      toast.error("An error occurred while deleting activity");
    } finally {
      setDeleteActivityLoading(false);
    }
  };

  const handleStatusChange = async ({ statusId, remarks }) => {
    if (!statusChangeActivity) return false;
    const followUpId =
      statusChangeActivity.FollowUpId ||
      statusChangeActivity.id ||
      statusChangeActivity.ActivityId;
    
    try {
      const result = await followUpActions.updateStatus(followUpId, {
        statusId,
        remarks,
        assignedTo: statusChangeActivity.AssignedTo || statusChangeActivity.assignedTo,
      });

      if (result?.success) {
        toast.success("Status updated successfully");
        setStatusChangeActivity(null);
        refreshFollowUps();
        invalidateNotesCache(leadId);
        return true;
      } else {
        toast.error(result?.error || "Failed to update status");
        return false;
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update status");
      return false;
    }
  };
  const popoverRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const isHoveringRef = useRef(false);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleItemHoverEnter = (event, item) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    isHoveringRef.current = true;

    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = rect.right + 8;
    let top = rect.top;

    if (left + 320 > viewportWidth) {
      left = rect.left - 328;
    }
    if (left < 8) {
      left = Math.max(8, (viewportWidth - 320) / 2);
    }
    if (top + 280 > viewportHeight) {
      top = Math.max(8, viewportHeight - 288);
    }

    setPopoverPosition({ top, left });
    setSelectedFollowUp(item);
  };

  const handleItemHoverLeave = () => {
    isHoveringRef.current = false;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        setSelectedFollowUp(null);
      }
    }, 200);
  };

  const handlePopoverMouseEnter = () => {
    isHoveringRef.current = true;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handlePopoverMouseLeave = () => {
    isHoveringRef.current = false;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        setSelectedFollowUp(null);
      }
    }, 200);
  };
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [selectedDateForModal, setSelectedDateForModal] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const { types: activityTypes, isLoading: loadingActivityTypes } =
    useActivityTypes();

  // Format date helpers - use local date to avoid timezone issues
  // Renamed to avoid conflict with potential imports and clarify purpose (returns YYYY-MM-DD)
  const getLocalISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper to construct ISO string from date and time parts for centralized utility
  const constructISO = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const timeComponent = timeStr
      ? timeStr.length > 5
        ? timeStr
        : timeStr + ":00"
      : "00:00:00";
    return `${dateStr.split("T")[0]}T${timeComponent}`;
  };

  // fetchFollowUps is handled by useLeadFollowUps hook; call refreshFollowUps() to re-fetch.

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setSelectedFollowUp(null);
      }
    };

    if (selectedFollowUp) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [selectedFollowUp]);

  // Handle follow-up click to show/hide popover
  const handleFollowUpClick = (event, followUp) => {
    event.stopPropagation();

    // If clicking the same follow-up, close the popover
    if (selectedFollowUp?.FollowUpId === followUp.FollowUpId) {
      setSelectedFollowUp(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate position - prefer right side, but flip if not enough space
    let left = rect.right + 8;
    let top = rect.top;

    // If popover would go off right edge, show on left side
    if (left + 320 > viewportWidth) {
      left = rect.left - 328;
    }

    // If popover would go off left edge, center it
    if (left < 8) {
      left = Math.max(8, (viewportWidth - 320) / 2);
    }

    // If popover would go off bottom, adjust top
    if (top + 280 > viewportHeight) {
      top = Math.max(8, viewportHeight - 288);
    }

    setPopoverPosition({ top, left });
    setSelectedFollowUp(followUp);
  };

  // Process follow-ups to add local date information
  const processedFollowUps = useMemo(() => {
    return followUps.map((fu) => {
      const dateStr = fu.ScheduledDate ? fu.ScheduledDate.split("T")[0] : "";
      let localDate = null;
      if (dateStr) {
        const [year, month, day] = dateStr.split("-").map(Number);
        localDate = new Date(year, month - 1, day);
      }
      const titleVal =
        fu.ActivityName ||
        fu.activityName ||
        fu.ActivityTitle ||
        fu.activityTitle ||
        fu.Title ||
        fu.title ||
        fu.LeadActivityTitle ||
        fu.leadActivityTitle ||
        "Activity";
      return {
        ...fu,
        _type: "activity",
        TaskName: titleVal,
        ActivityName: titleVal,
        activityName: titleVal,
        _localDateObj: localDate,
        _localDateStr: dateStr,
        _localTimeStr: formatTimeLocal(fu.ScheduledTime),
      };
    });
  }, [followUps]);

  // Status-id → style mapping (shared with CalendarDayCell via getItemStyle)
  const getCalendarStatusStyle = (statusId) => {
    switch (statusId) {
      case 1: return { bg: "bg-yellow-100 dark:bg-yellow-900/20", text: "text-yellow-800 dark:text-yellow-200", border: "border-yellow-200 dark:border-yellow-800", dot: "bg-yellow-500", icon: PendingIcon,            iconColor: "text-yellow-600 dark:text-yellow-400" };
      case 2: return { bg: "bg-green-100 dark:bg-green-900/20",   text: "text-green-800 dark:text-green-200",   border: "border-green-200 dark:border-green-800",   dot: "bg-green-500",  icon: CheckCircleIcon,           iconColor: "text-green-600 dark:text-green-400" };
      case 3: return { bg: "bg-orange-100 dark:bg-orange-900/20", text: "text-orange-800 dark:text-orange-200", border: "border-orange-200 dark:border-orange-800", dot: "bg-orange-500", icon: ExclamationTriangleIcon,    iconColor: "text-orange-600 dark:text-orange-400" };
      case 4: return { bg: "bg-gray-100 dark:bg-gray-800",        text: "text-gray-800 dark:text-gray-200",    border: "border-gray-200 dark:border-gray-700",    dot: "bg-gray-500",  icon: NoSymbolIcon,              iconColor: "text-gray-600 dark:text-gray-400" };
      case 5: return { bg: "bg-yellow-100 dark:bg-yellow-900/20", text: "text-yellow-800 dark:text-yellow-200", border: "border-yellow-200 dark:border-yellow-800", dot: "bg-yellow-500", icon: PendingIcon,            iconColor: "text-yellow-600 dark:text-yellow-400" };
      case 6: return { bg: "bg-red-100 dark:bg-red-900/20",       text: "text-red-800 dark:text-red-200",      border: "border-red-200 dark:border-red-800",      dot: "bg-red-650",   icon: ExclamationCircleIcon,     iconColor: "text-red-650 dark:text-red-400" };
      case 7: return { bg: "bg-red-100 dark:bg-red-900/20",       text: "text-red-800 dark:text-red-200",      border: "border-red-200 dark:border-red-800",      dot: "bg-red-500",   icon: XCircleIcon,               iconColor: "text-red-650 dark:text-red-400" };
      default:return { bg: "bg-blue-100 dark:bg-blue-900/20",     text: "text-blue-800 dark:text-blue-200",    border: "border-blue-200 dark:border-blue-800",    dot: "bg-blue-500",  icon: ClockIcon,                 iconColor: "text-blue-600 dark:text-blue-400" };
    }
  };

  // Render a single calendar day cell using CustomCalendar callback
  const renderCalendarDay = (dayInfo, index) => {
    const dateStr = getLocalISODate(dayInfo.date);
    const dayFollowUps = processedFollowUps.filter(
      (fu) => fu._localDateStr === dateStr,
    );

    const isPast = dayInfo.date.getTime() < new Date().setHours(0, 0, 0, 0);
    const canSchedule = hasPermission("FUPSCFUP") && !isStudent && !isPast;

    const getItemStyle = (followUp) => getCalendarStatusStyle(followUp.StatusId || 1);
    const getItemName = (followUp) =>
      `${followUp._localTimeStr || formatTimeLocal(followUp.ScheduledTime)}${followUp.Remarks ? ` - ${followUp.Remarks}` : ""}`;

    const handleItemClick = (followUp) => {
      setSelectedFollowUp(null);
      setEditingFollowUp({
        ...followUp,
        id: followUp.FollowUpId || followUp.id || followUp.ActivityId,
        leadId: followUp.LeadId || followUp.leadId || leadId,
        isReschedule: false,
      });
    };

    const handleAddActivity = (date, type) => {
      setSelectedDateForModal(date);
      setSelectedActivityType(type);
      setShowScheduleModal(true);
    };

    return (
      <div key={index} className="relative h-full w-full">
        <CalendarDayCell
          day={dayInfo}
          idx={index}
          items={dayFollowUps}
          onCellClick={undefined}
          onAddActivity={handleAddActivity}
          onItemClick={handleItemClick}
          activityTypes={activityTypes}
          loadingActivityTypes={loadingActivityTypes}
          canSchedule={canSchedule}
          wholeCellMenu={canSchedule}
          getItemStyle={getItemStyle}
          getItemName={getItemName}
          handleItemHoverEnter={handleItemHoverEnter}
          handleItemHoverLeave={handleItemHoverLeave}
          formatTimeLocal={formatTimeLocal}
        />

        {/* Day indicator dot for follow-ups (top-right corner) */}
        {dayFollowUps.length > 0 && (
          <div className="absolute top-1 right-1 pointer-events-none z-10">
            <div
              className={`h-2 w-2 rounded-full ${(() => {
                if (dayFollowUps.some((fu) => fu.StatusId === 7)) return "bg-red-500";
                if (dayFollowUps.some((fu) => fu.StatusId === 6)) return "bg-red-650";
                if (dayFollowUps.some((fu) => fu.StatusId === 3)) return "bg-orange-500";
                if (dayFollowUps.some((fu) => fu.StatusId === 2)) return "bg-green-500";
                if (dayFollowUps.some((fu) => fu.StatusId === 4)) return "bg-gray-500";
                return "bg-yellow-500";
              })()}`}
            />
          </div>
        )}
      </div>
    );
  };



  // Navigation functions
  const goToPrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  // Handle follow-up scheduling with hook (auto-invalidates SWR cache)

  // Handle successful follow-up creation (for modal close)
  const handleFollowUpCreated = () => {
    setShowScheduleModal(false);
  };

  if (loading) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
        <div className="flex items-center justify-center">
          <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-b-2"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">
            Loading calendar...
          </span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
        <div className="mb-4 text-red-600 dark:text-red-400">
          Error loading calendar: {error}
        </div>
        <Button variant="outlined" color="primary" onClick={refreshFollowUps}>
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <>
      <div className={isMobile ? "space-y-3" : "space-y-6"}>
        {/* Header and Controls Card */}
        <Card
          className={
            isMobile ? "border-none bg-transparent p-3 shadow-none" : "p-6"
          }
        >
          <div
            className={`flex ${isMobile ? "flex-col items-stretch gap-2.5" : "flex-col items-start justify-between gap-4 sm:flex-row sm:items-center"} ${viewMode === "calendar" && !isMobile ? "mb-6" : ""}`}
          >
            <div className="flex items-center gap-3">
              {viewMode === "calendar" ? (
                <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
              ) : (
                <ListBulletIcon className="h-6 w-6 text-blue-600" />
              )}
              <h3
                className={`${isMobile ? "text-base" : "text-xl"} font-semibold text-gray-900 dark:text-gray-100`}
              >
                {viewMode === "calendar"
                  ? "Activity Calendar"
                  : "Activities List"}
              </h3>
            </div>

            <div
              className={`flex w-full items-center gap-2 ${isMobile ? "justify-between" : "flex-wrap justify-end sm:w-auto md:gap-4"}`}
            >
              <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  onClick={() => setViewMode("calendar")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === "calendar"
                      ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <CalendarDaysIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Calendar</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === "list"
                      ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <ListBulletIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>

              {hasPermission("FUPSCFUP") ? (
                <Menu as="div" className="relative inline-block text-left">
                  <MenuButton as={Fragment}>
                    <Button
                      color="primary"
                      variant="soft"
                      size="sm"
                      disabled={isStudent}
                      className={
                        isStudent ? "cursor-not-allowed opacity-60" : ""
                      }
                    >
                      <PlusIcon className="mr-2 h-4 w-4" />
                      {isMobile ? "Schedule" : "Schedule Activity"}
                      <ChevronDownIcon className="ml-1 h-4 w-4 opacity-60" />
                    </Button>
                  </MenuButton>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="dark:bg-dark-700 absolute right-0 z-50 mt-2 max-h-60 w-48 origin-top-right overflow-y-auto rounded-xl border border-gray-200/60 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)] ring-1 ring-black/10 focus:outline-none dark:border-gray-700/50 dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                      <div className="p-1">
                        {loadingActivityTypes ? (
                          <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-400">
                            <div className="border-primary-400 h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" />
                            Loading...
                          </div>
                        ) : activityTypes.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-gray-400">
                            No types available
                          </div>
                        ) : (
                          activityTypes.map((type) => (
                            <MenuItem key={type.id ?? type.channelId}>
                              {({ active }) => (
                                <button
                                  onClick={() => {
                                    setSelectedDateForModal(null);
                                    setSelectedActivityType(type);
                                    setShowScheduleModal(true);
                                  }}
                                  className={clsx(
                                    "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                                    active
                                      ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20"
                                      : "dark:text-dark-200 text-gray-700",
                                  )}
                                >
                                  {type.label}
                                </button>
                              )}
                            </MenuItem>
                          ))
                        )}
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
              ) : null}
            </div>
          </div>

          {viewMode === "calendar" ? (
            <CustomCalendar
              currentDate={currentDate}
              onPrev={goToPrevMonth}
              onNext={goToNextMonth}
              className="mt-1"
              dayHeaderClassName="grid grid-cols-7 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
              dayNameClassName="border-r border-gray-200 p-3 text-center text-sm font-semibold text-gray-700 last:border-r-0 dark:border-gray-700 dark:text-gray-300"
              gridWrapperClassName="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
              renderDay={renderCalendarDay}
            />
          ) : (
            <div className={isMobile ? "mt-1" : "mt-3"}>
              {/* Follow-ups List */}
              {followUps.length > 0 && (
                <Card
                  className={
                    isMobile
                      ? "border-none bg-transparent p-0 shadow-none"
                      : "p-6"
                  }
                >
                  <div
                    className={`flex items-center justify-between ${isMobile ? "mb-3" : "mb-6"}`}
                  >
                    <h4
                      className={`${isMobile ? "text-sm" : "text-lg"} font-semibold text-gray-900 dark:text-gray-100`}
                    >
                      {viewMode === "calendar"
                        ? "All Activities"
                        : "All Activities"}
                    </h4>
                    <Badge variant="soft" color="primary">
                      {processedFollowUps.length} total
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    {processedFollowUps
                      .slice(0, viewMode === "list" ? undefined : 5)
                      .map((followUp, index) => {
                        // Get status information from new API structure
                        const statusId = followUp.StatusId || 1;
                        const statusName = followUp.StatusName || "Pending";
                        const outcomeId = followUp.OutcomeId;
                        const eventName = followUp.EventName;

                        // Define status configurations based on your status table
                        const getStatusConfig = (statusId, statusName) => {
                          switch (statusId) {
                            case 1: // Pending
                              return {
                                name: "Pending",
                                color: "warning",
                                bgColor: "bg-yellow-50 dark:bg-yellow-900/10",
                                borderColor:
                                  "border-yellow-200 dark:border-yellow-800",
                                indicatorColor: "bg-yellow-500",
                                icon: PendingIcon,
                                iconColor:
                                  "text-yellow-600 dark:text-yellow-400",
                              };
                            case 2: // Completed
                              return {
                                name: "Completed",
                                color: "success",
                                bgColor: "bg-green-50 dark:bg-green-900/10",
                                borderColor:
                                  "border-green-200 dark:border-green-800",
                                indicatorColor: "bg-green-500",
                                icon: CheckCircleIcon,
                                iconColor: "text-green-600 dark:text-green-400",
                              };
                            case 3: // Missed
                              return {
                                name: "Missed",
                                color: "warning",
                                bgColor: "bg-orange-50 dark:bg-orange-900/10",
                                borderColor:
                                  "border-orange-200 dark:border-orange-800",
                                indicatorColor: "bg-orange-500",
                                icon: ExclamationTriangleIcon,
                                iconColor:
                                  "text-orange-600 dark:text-orange-400",
                              };
                            case 4: // Cancelled
                              return {
                                name: "Cancelled",
                                color: "neutral",
                                bgColor: "bg-gray-50 dark:bg-gray-800",
                                borderColor:
                                  "border-gray-200 dark:border-gray-700",
                                indicatorColor: "bg-gray-500",
                                icon: NoSymbolIcon,
                                iconColor: "text-gray-600 dark:text-gray-400",
                              };
                            case 7: // Failed
                              return {
                                name: "Failed",
                                color: "error",
                                bgColor: "bg-red-50 dark:bg-red-900/10",
                                borderColor:
                                  "border-red-200 dark:border-red-800",
                                indicatorColor: "bg-red-500",
                                icon: XCircleIcon,
                                iconColor: "text-red-600 dark:text-red-400",
                              };
                            case 6: // Overdue
                              return {
                                name: "Overdue",
                                color: "error",
                                bgColor: "bg-red-50 dark:bg-red-900/10",
                                borderColor:
                                  "border-red-200 dark:border-red-800",
                                indicatorColor: "bg-red-600",
                                icon: ExclamationCircleIcon,
                                iconColor: "text-red-600 dark:text-red-400",
                              };
                            case 5: //rescheduled
                              return {
                                name: "ReScheduled",
                                color: "warning",
                                bgColor: "bg-yellow-50 dark:bg-yellow-900/10",
                                borderColor:
                                  "border-yellow-200 dark:border-yellow-800",
                                indicatorColor: "bg-yellow-500",
                                icon: PendingIcon,
                                iconColor:
                                  "text-yellow-600 dark:text-yellow-400",
                              };
                            default:
                              return {
                                name: statusName || "Unknown",
                                color: "neutral",
                                bgColor: "bg-gray-50 dark:bg-gray-800",
                                borderColor:
                                  "border-gray-200 dark:border-gray-700",
                                indicatorColor: "bg-gray-400",
                                icon: ClockIcon,
                                iconColor: "text-gray-600 dark:text-gray-400",
                              };
                          }
                        };

                        const status = getStatusConfig(
                          followUp?.StatusId,
                          followUp?.StatusName,
                        );
                        const followUpDate =
                          followUp._localDateObj ||
                          getLocalDateTime(
                            constructISO(
                              followUp.ScheduledDate,
                              followUp.ScheduledTime,
                            ),
                          );
                        const isToday =
                          getLocalISODate(followUpDate) ===
                          getLocalISODate(new Date());
                        const isPast =
                          followUpDate < new Date().setHours(0, 0, 0, 0);

                        return (
                          <div
                            key={index}
                            onClick={() => {
                              setEditingFollowUp({
                                ...followUp,
                                id:
                                  followUp.FollowUpId ||
                                  followUp.id ||
                                  followUp.ActivityId,
                                leadId:
                                  followUp.LeadId || followUp.leadId || leadId,
                                isReschedule: false,
                              });
                              setShowScheduleModal(true);
                            }}
                            className={`relative ${isMobile ? "p-3" : "p-4"} cursor-pointer rounded-xl border transition-all duration-200 hover:shadow-md ${status.bgColor} ${status.borderColor} `}
                          >
                            {/* Status Indicator */}
                            <div
                              className={`absolute top-0 bottom-0 left-0 w-1 rounded-l-xl ${status.indicatorColor} `}
                            ></div>

                            <div className="ml-3 flex items-start justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                {/* Follow-up Title */}
                                <div
                                  className={`flex items-center gap-2 ${isMobile ? "mb-1" : "mb-2"}`}
                                >
                                  <h5
                                    className={`font-semibold text-gray-900 dark:text-gray-100 ${isMobile ? "text-sm" : ""}`}
                                  >
                                    {followUp?.ActivityName
                                      ? followUp?.ActivityName
                                      : followUp.status == "ReScheduled"
                                        ? "Activity Re-Scheduled"
                                        : "Activity Scheduled"}
                                  </h5>
                                  {isToday && (
                                    <Badge
                                      variant="soft"
                                      color="primary"
                                      size="sm"
                                    >
                                      Today
                                    </Badge>
                                  )}
                                  {isPast && statusId === 1 && (
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setStatusChangeActivity(followUp);
                                      }}
                                      className="cursor-pointer hover:opacity-80 transition-opacity"
                                    >
                                      <Badge
                                        variant="soft"
                                        color="error"
                                        size="sm"
                                      >
                                        Overdue
                                      </Badge>
                                    </div>
                                  )}
                                </div>

                                {/* Follow-up Details */}
                                <div
                                  className={`flex flex-wrap items-center ${isMobile ? "gap-x-2.5 gap-y-1 text-xs" : "gap-4 text-sm"} text-gray-600 dark:text-gray-400`}
                                >
                                  <div className="flex items-center gap-1">
                                    <ClockIcon
                                      className={
                                        isMobile ? "h-3.5 w-3.5" : "h-4 w-4"
                                      }
                                    />
                                    <span className="font-medium">
                                      {(() => {
                                        const dVal = String(
                                          followUpDate.getDate(),
                                        ).padStart(2, "0");
                                        const mVal = String(
                                          followUpDate.getMonth() + 1,
                                        ).padStart(2, "0");
                                        const yVal = String(
                                          followUpDate.getFullYear(),
                                        ).slice(-2);
                                        return `${dVal}/${mVal}/${yVal}`;
                                      })()}
                                    </span>
                                    <span className="text-gray-400">at</span>
                                    <span className="font-medium">
                                      {followUp._localTimeStr ||
                                        formatTimeLocal(followUp.ScheduledTime)}
                                    </span>
                                  </div>

                                  {followUp.AssignedToName && (
                                    <div className="flex items-center gap-1">
                                      <UserIcon
                                        className={
                                          isMobile ? "h-3.5 w-3.5" : "h-4 w-4"
                                        }
                                      />
                                      <span>{followUp.AssignedToName}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Status Badge */}
                              <div
                                className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (statusId !== 2) {
                                    setStatusChangeActivity(followUp);
                                  }
                                }}
                              >
                                <Badge
                                  variant="soft"
                                  color={status.color}
                                  size={isMobile ? "sm" : undefined}
                                >
                                  <div className="flex items-center gap-1">
                                    <status.icon
                                      className={`h-3 w-3 ${status.iconColor}`}
                                    />
                                    {followUp?.status
                                      ? followUp?.status
                                      : status.name}
                                  </div>
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    {viewMode === "calendar" && followUps.length > 5 && (
                      <div className="border-t border-gray-200 py-4 text-center dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          And {processedFollowUps.length - 5} more activities...
                        </p>
                        <Button
                          variant="outlined"
                          size="sm"
                          className="mt-2"
                          onClick={() => setViewMode("list")}
                        >
                          View All Activities
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Empty State */}
              {followUps.length === 0 && (
                <Card
                  className={`${isMobile ? "border-none bg-transparent p-4 shadow-none" : "p-8"} text-center`}
                >
                  <div
                    className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} mx-auto mb-4 flex items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/20`}
                  >
                    <CalendarDaysIcon
                      className={`${isMobile ? "h-6 w-6" : "h-8 w-8"} text-indigo-600 dark:text-indigo-400`}
                    />
                  </div>
                  <h3
                    className={`${isMobile ? "text-base" : "text-lg"} dark:text-dark-50 mb-2 font-semibold text-gray-900`}
                  >
                    No Activities Scheduled
                  </h3>
                  <p
                    className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-300 text-gray-500 ${isMobile ? "mb-4" : "mb-6"}`}
                  >
                    Schedule activities to track and manage interactions with
                    this lead
                  </p>
                  <Menu as="div" className="relative inline-block text-left">
                    <MenuButton as={Fragment}>
                      <Button
                        variant="soft"
                        color="primary"
                        disabled={isStudent}
                        className={`${isMobile ? "px-4 py-2 text-sm" : ""} ${isStudent ? "cursor-not-allowed opacity-60" : ""}`}
                      >
                        <PlusIcon className="mr-2 h-4 w-4" />
                        {isMobile
                          ? "Schedule First"
                          : "Schedule First Follow-up"}
                        <ChevronDownIcon className="ml-1 h-4 w-4 opacity-60" />
                      </Button>
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="dark:bg-dark-700 absolute left-1/2 z-50 mt-2 max-h-60 w-48 origin-top -translate-x-1/2 overflow-y-auto rounded-xl border border-gray-200/60 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)] ring-1 ring-black/10 focus:outline-none dark:border-gray-700/50 dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                        <div className="p-1">
                          {loadingActivityTypes ? (
                            <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-400">
                              <div className="border-primary-400 h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" />
                              Loading...
                            </div>
                          ) : activityTypes.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-gray-400">
                              No types available
                            </div>
                          ) : (
                            activityTypes.map((type) => (
                              <MenuItem key={type.id ?? type.channelId}>
                                {({ active }) => (
                                  <button
                                    onClick={() => {
                                      setSelectedDateForModal(null);
                                      setSelectedActivityType(type);
                                      setShowScheduleModal(true);
                                    }}
                                    className={clsx(
                                      "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                                      active
                                        ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20"
                                        : "dark:text-dark-200 text-gray-700",
                                    )}
                                  >
                                    {type.label}
                                  </button>
                                )}
                              </MenuItem>
                            ))
                          )}
                          <div className="my-1 border-t border-gray-200 dark:border-dark-600" />
                          <MenuItem>
                            {({ active }) => (
                              <button
                                onClick={() => {
                                  setSelectedDateForModal(null);
                                  setSelectedActivityType(null);
                                  setShowScheduleModal(true);
                                }}
                                className={clsx(
                                  "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                                  active
                                    ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20"
                                    : "dark:text-dark-200 text-gray-700",
                                )}
                              >
                                Custom Activity
                              </button>
                            )}
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </Card>
              )}
            </div>
          )}
        </Card>
        {/* Google Calendar Style Popover */}
        <FloatingPopoverTooltip
          ref={popoverRef}
          selectedFollowUp={selectedFollowUp ? {
            ...selectedFollowUp,
            LeadName: selectedFollowUp.LeadName || selectedFollowUp.LeadTitle || leadData?.LeadName || leadData?.name || (leadData?.FirstName ? `${leadData.FirstName} ${leadData.LastName || ''}`.trim() : `Lead #${leadId}`)
          } : null}
          position={popoverPosition}
          onClose={() => setSelectedFollowUp(null)}
          onViewDetails={(item) => {
            setSelectedFollowUp(null);
            setEditingFollowUp({
              ...item,
              id: item.FollowUpId || item.id || item.ActivityId,
              leadId: item.LeadId || item.leadId || leadId,
              isReschedule: false,
            });
            setShowScheduleModal(true);
          }}
          onDelete={(item) => {
            setSelectedFollowUp(null);
            setActivityToDelete(item);
          }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        />
      </div>

      {/* Schedule Follow-up Modal */}
      <FollowUpScheduleModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedActivityType(null);
          setSelectedDateForModal(null);
          setEditingFollowUp(null);
        }}
        leadData={{ ...leadData, id: leadId }}
        preSelectedActivityType={selectedActivityType}
        enableLeadSelection={false}
        selectedDate={selectedDateForModal}
        editingFollowUp={editingFollowUp}
        onSuccess={() => {
          setShowScheduleModal(false);
          setSelectedActivityType(null);
          setSelectedDateForModal(null);
          setEditingFollowUp(null);
          refreshFollowUps();
          invalidateNotesCache(leadId);
        }}
        restrictedEditMode={false}
      />

      <ActivityStatusChangeModal
        isOpen={!!statusChangeActivity}
        onClose={() => setStatusChangeActivity(null)}
        count={statusChangeActivity ? 1 : 0}
        onStatusChange={handleStatusChange}
        initialStatusId={initialStatusId}
      />

      <ConfirmModal
        show={!!activityToDelete}
        onClose={() => setActivityToDelete(null)}
        onOk={confirmDeleteActivity}
        confirmLoading={deleteActivityLoading}
        buttonColor="error"
        messages={{
          pending: {
            title: "Delete Activity",
            description: `Are you sure you want to delete "${activityToDelete?.ActivityTitle || activityToDelete?.activityName || activityToDelete?.Remarks || "this activity"}"? This action cannot be undone.`,
            actionText: "Delete",
          }
        }}
      />
    </>
  );
};

export default CalendarTab;
